import { Check, Circle, Plane, X } from 'lucide-react';

import type { DevicePhaseView } from '../lib/devicePhase';
import { STROKE, ACCENT, STATE, TEXT } from '../theme/tokens';

// The device migration stepper: Ready → Flight Plan → Pushback → Enroute →
// Arrival → Complete.
//
// It renders `DevicePhaseView` and derives nothing itself — the phase is
// computed in ../lib/devicePhase.ts from the device row and its progress events,
// where it is unit-tested. A stepper that inferred its own state from props
// would be a second implementation of the same rule.

const ICON_SIZE = 15;

export default function PhaseStepper({ view }: { view: DevicePhaseView }) {
  return (
    <ol className="m-0 flex list-none items-start gap-0 p-0">
      {view.phases.map((phase, index) => {
        const isLast = index === view.phases.length - 1;
        const tone =
          phase.state === 'failed'
            ? STATE.critical
            : phase.state === 'stopped'
              ? STATE.warning
              : phase.state === 'done'
                ? STATE.success
                : null;

        const circleBg =
          phase.state === 'current'
            ? ACCENT.base
            : tone
              ? tone.glyph
              : STROKE.base;
        const circleFg = phase.state === 'pending' ? TEXT.secondary : '#FFFFFF';

        return (
          <li
            key={phase.id}
            className="flex min-w-0 flex-1 flex-col items-center"
            // FD-467 — the stepper drew its current step in the accent and
            // nothing else. Colour alone is not a state a screen reader can
            // read, and `aria-current="step"` is the one attribute a stepper
            // exists to carry.
            aria-current={phase.state === 'current' ? 'step' : undefined}
          >
            <div className="flex w-full items-center">
              {/* Leading connector — hidden on the first step so the row does
                  not start with a dangling line. */}
              <span
                aria-hidden="true"
                className="h-[2px] flex-1"
                style={{
                  backgroundColor:
                    index === 0
                      ? 'transparent'
                      : view.phases[index - 1].state === 'done'
                        ? STATE.success.glyph
                        : STROKE.base,
                }}
              />
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                style={{ backgroundColor: circleBg, color: circleFg }}
              >
                {phase.state === 'done' ? (
                  <Check size={ICON_SIZE} strokeWidth={2} />
                ) : phase.state === 'failed' ? (
                  <X size={ICON_SIZE} strokeWidth={2} />
                ) : phase.state === 'current' ? (
                  <Plane size={ICON_SIZE} strokeWidth={2} />
                ) : (
                  <Circle size={7} strokeWidth={3} />
                )}
              </span>
              <span
                aria-hidden="true"
                className="h-[2px] flex-1"
                style={{
                  backgroundColor:
                    isLast
                      ? 'transparent'
                      : phase.state === 'done'
                        ? STATE.success.glyph
                        : STROKE.base,
                }}
              />
            </div>
            <div className="mt-2 px-1 text-center">
              <div
                className="text-[12px]"
                style={{
                  color: TEXT.primary,
                  fontWeight: phase.state === 'current' ? 600 : 400,
                }}
              >
                {phase.label}
              </div>
              {phase.detail && (
                <div className="mt-0.5 text-[10px]" style={{ color: TEXT.secondary }}>
                  {phase.detail}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
